// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';

import FileTypeEditor from 'view/Pages/CatalogsPage/FileTypeEditor';
import FileTypeComponent from 'view/Pages/CatalogsPage/FileTypeComponent';
import Listbox from 'view/Forms/Listbox';
import { computeFileTypeName } from 'util/catalogUtils';
import localizedStrings from 'util/localizedStrings';
import { actions as modalPrompt } from 'portfolio-shared/state/reducers/modalPromptReducer';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import { getCatalogExclusionInfo } from 'state/selectors';
import {
  restoreFileTypesDefault,
  setExclusionInfo,
} from 'state/commands/catalogCommands';
import type { ListboxSelectionState } from 'view/Forms/Listbox';
import type { Catalog, Dispatch, ExclusionInfo, FileType } from 'state/types';

const { showModalPrompt } = modalPrompt;

type Props = {
  catalog: Catalog,
  exclusionInfo: ExclusionInfo,
  intl: IntlShape,
  restoreFileTypesDefault: typeof restoreFileTypesDefault,
  setExclusionInfo: typeof setExclusionInfo,
  showModalPrompt: typeof showModalPrompt,
};

type State = {
  allowAllFileTypes: boolean,
  selectedFileTypeName: ?string,
  selectedFileType: ?FileType,
  isAddFileType: boolean,
  editorHasUnsavedChanges: boolean,
  listboxSelection: ListboxSelectionState,
};

const initialState: State = {
  allowAllFileTypes: true,
  selectedFileTypeName: undefined,
  selectedFileType: undefined,
  isAddFileType: false,
  editorHasUnsavedChanges: false,
  listboxSelection: {
    selectedItems: [],
    lastClicked: undefined,
    lastArrowed: undefined,
  },
};

class FileTypesComponent extends Component<Props, State> {
  fileTypeToComponent: Function;
  renderFileTypesComponent: Function;
  handleAddFileType: Function;
  handleAddFileType_postPrompt: Function;
  handleCancelFileTypeEdit: Function;
  handleRemoveFileType: Function;
  handleSelectFileType: Function;
  handleSelectFileType_postPrompt: Function;
  handleToggleFileTypeEnabled: Function;
  fileTypeEditor: typeof FileTypeEditor;
  setEditorHasUnsavedChanges: Function;
  setFileTypes: Function;
  handleRestoreDefaults: Function;
  onChangeAllowFileTypes: Function;

  state = initialState;

  constructor() {
    super();
    this.fileTypeToComponent = this.fileTypeToComponent.bind(this);
    this.onChangeAllowFileTypes = this.onChangeAllowFileTypes.bind(this);
    this.handleAddFileType = this.handleAddFileType.bind(this);
    this.handleAddFileType_postPrompt = this.handleAddFileType_postPrompt.bind(
      this
    );
    this.handleSelectFileType_postPrompt = this.handleSelectFileType_postPrompt.bind(
      this
    );
    this.handleCancelFileTypeEdit = this.handleCancelFileTypeEdit.bind(this);
    this.handleRemoveFileType = this.handleRemoveFileType.bind(this);
    this.handleSelectFileType = this.handleSelectFileType.bind(this);
    this.handleToggleFileTypeEnabled = this.handleToggleFileTypeEnabled.bind(
      this
    );
    this.setFileTypes = this.setFileTypes.bind(this);
    this.setEditorHasUnsavedChanges = this.setEditorHasUnsavedChanges.bind(
      this
    );
    this.handleRestoreDefaults = this.handleRestoreDefaults.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.exclusionInfo) {
      this.setState({
        allowAllFileTypes: nextProps.exclusionInfo.allowAllFileTypes,
      });
    }
  }

  onChangeAllowFileTypes(e) {
    const { catalog, exclusionInfo } = this.props;
    const allowAll = e.currentTarget.value === 'allFileTypes';
    this.props.setExclusionInfo(catalog.catalogId, {
      ...exclusionInfo,
      allowAllFileTypes: allowAll,
    });
    this.setState({ allowAllFileTypes: allowAll });
  }

  handleToggleFileTypeEnabled(enabled: boolean, name: string, ft: FileType) {
    this.setFileTypes(
      this.props.exclusionInfo.fileTypes.map(f => {
        return f.title === ft.title && f.extensions === ft.extensions
          ? { title: ft.title, extensions: ft.extensions, enabled }
          : f;
      })
    );
  }

  async setFileTypes(fileTypes: Array<FileType>, newFileType: FileType) {
    const { catalog, exclusionInfo } = this.props;
    await this.props.setExclusionInfo(catalog.catalogId, {
      ...exclusionInfo,
      fileTypes: fileTypes.map(ft => {
        return {
          title: ft.title,
          extensions: ft.extensions,
          enabled: ft.enabled,
        };
      }),
    });
    if (this.state.isAddFileType) {
      const name = computeFileTypeName(newFileType);
      this.setState({
        selectedFileTypeName: name,
        selectedFileType: newFileType,
        editorHasUnsavedChanges: false,
        isAddFileType: false,
        listboxSelection: {
          selectedItems: [name],
          lastClicked: name,
          lastArrowed: name,
        },
      });
    } else {
      this.setState({ editorHasUnsavedChanges: false });
    }
  }

  handleSelectFileType_postPrompt(selectionState: Object) {
    if (!selectionState) {
      return;
    }

    if (selectionState.selectedItems.length === 1) {
      const ft: ?FileType = this.props.exclusionInfo.fileTypes.find(
        f => computeFileTypeName(f) === selectionState.selectedItems[0]
      );
      this.setState({
        selectedFileTypeName: ft ? computeFileTypeName(ft) : undefined,
        selectedFileType: ft,
        editorHasUnsavedChanges: false,
        isAddFileType: false,
        listboxSelection: selectionState,
      });
    } else {
      this.setState({
        selectedFileTypeName: undefined,
        selectedFileType: undefined,
        editorHasUnsavedChanges: false,
        isAddFileType: false,
        listboxSelection: selectionState,
      });
    }
  }

  handleSelectFileType(selectionState: Object) {
    if (this.state.editorHasUnsavedChanges) {
      this.props.showModalPrompt({
        message: localizedStrings.FILE_TYPES_PROMPT_DISCARD_CHANGES,
        onButton2: () => this.handleSelectFileType_postPrompt(selectionState),
      });
    } else {
      this.handleSelectFileType_postPrompt(selectionState);
    }
  }

  handleAddFileType_postPrompt() {
    this.setState({
      selectedFileTypeName: undefined,
      selectedFileType: undefined,
      isAddFileType: true,
    });
  }

  handleAddFileType() {
    if (this.state.editorHasUnsavedChanges) {
      this.props.showModalPrompt({
        message: localizedStrings.FILE_TYPES_PROMPT_DISCARD_CHANGES,
        onButton2: () => this.handleAddFileType_postPrompt(),
      });
    } else {
      this.handleAddFileType_postPrompt();
    }
  }

  handleRemoveFileType() {
    const {
      exclusionInfo: { fileTypes },
      showModalPrompt,
    } = this.props;
    const { selectedItems } = this.state.listboxSelection;
    showModalPrompt({
      message: localizedStrings.CATALOG_OPTIONS_REMOVE_FILE_TYPES_CONFIRMATION,
      onButton2: () => {
        this.setFileTypes(
          fileTypes.filter(
            ft => !selectedItems.includes(computeFileTypeName(ft))
          )
        );
        this.setState({
          selectedFileTypeName: undefined,
          selectedFileType: undefined,
          listboxSelection: {
            selectedItems: [],
            lastClicked: undefined,
            lastArrowed: undefined,
          },
        });
      },
    });
  }

  handleRestoreDefaults() {
    const { catalog, restoreFileTypesDefault, showModalPrompt } = this.props;
    const { allowAllFileTypes } = this.state;

    showModalPrompt({
      message: localizedStrings.FILE_TYPES_PROMPT_RESTORE_DEFAULTS,
      onButton2: () => {
        restoreFileTypesDefault(catalog.catalogId);
        this.setState({ ...initialState, allowAllFileTypes });
      },
    });
  }

  handleCancelFileTypeEdit() {
    this.setState({ isAddFileType: false, editorHasUnsavedChanges: false });
  }

  setEditorHasUnsavedChanges(v: boolean) {
    this.setState({ editorHasUnsavedChanges: v });
  }

  fileTypeToComponent(
    item,
    isSelected: boolean,
    onClick: Function,
    onKeyDown: Function,
    disabled
  ) {
    const name = computeFileTypeName(item);
    return (
      <FileTypeComponent
        disabled={disabled}
        key={name}
        name={name}
        fileType={item}
        onClick={onClick}
        onKeyDown={onKeyDown}
        selectFileType={this.handleSelectFileType}
        toggleFileTypeEnabled={this.handleToggleFileTypeEnabled}
        isSelected={isSelected}
      />
    );
  }

  render() {
    const { exclusionInfo } = this.props;
    const allowAllFileTypes = this.state.allowAllFileTypes;
    const fileTypes = ((exclusionInfo && exclusionInfo.fileTypes) || [])
      .map(f => {
        return { ...f, listId: computeFileTypeName(f) };
      })
      .sort((a, b) => a.listId.localeCompare(b.listId));

    return (
      <section className="box" style={{ height: '600px' }}>
        <h2 className="subtitle">
          <FormattedMessage {...localizedStrings.FILE_TYPES_SECTION_TITLE} />
        </h2>
        <div
          className="control"
          key="allFileTypes"
          style={{ marginBottom: '.5rem' }}
        >
          <input
            checked={allowAllFileTypes}
            className="is-checkradio"
            value="allFileTypes"
            id="allFileTypes"
            onChange={this.onChangeAllowFileTypes}
            type="radio"
            data-test-name="radio-allFileTypes"
          />
          <label className="role-label" htmlFor="allFileTypes">
            <FormattedMessage
              {...localizedStrings.CATALOG_OPTIONS_ALL_FILE_TYPES}
            />
          </label>
        </div>
        <div
          className="control"
          key="onlyTheseFileTypes"
          style={{ marginBottom: '.5rem' }}
        >
          <input
            checked={!allowAllFileTypes}
            className="is-checkradio"
            value="onlyTheseFileTypes"
            id="onlyTheseFileTypes"
            onChange={this.onChangeAllowFileTypes}
            type="radio"
            data-test-name="radio-onlyTheseFileTypes"
          />
          <label className="role-label" htmlFor="onlyTheseFileTypes">
            <FormattedMessage
              {...localizedStrings.CATALOG_OPTIONS_ONLY_FILE_TYPES}
            />
          </label>
        </div>
        {!allowAllFileTypes && (
          <div>
            <div className="box filetype-main-box" key="fileTypesManager">
              <div className="control filetype-list" key="fileTypesList">
                <Listbox
                  outerClass="select is-multiple is-small"
                  items={fileTypes}
                  itemToComponent={this.fileTypeToComponent}
                  selectionState={this.state.listboxSelection}
                  disabled={allowAllFileTypes}
                />
              </div>
              <FileTypeEditor
                disabled={
                  allowAllFileTypes ||
                  (!this.state.isAddFileType &&
                    !this.state.selectedFileTypeName)
                }
                fileType={this.state.selectedFileType}
                fileTypeName={this.state.selectedFileTypeName}
                fileTypes={fileTypes}
                setFileTypes={this.setFileTypes}
                isAdd={this.state.isAddFileType}
                onCancel={this.handleCancelFileTypeEdit}
                setHasUnsavedChanges={this.setEditorHasUnsavedChanges}
                ref={c => (this.fileTypeEditor = c)}
              />
            </div>
            <div
              className="field is-grouped is-pulled-left"
              style={{ marginLeft: '40px' }}
            >
              <div className="control">
                <button
                  className="button"
                  onClick={this.handleAddFileType}
                  type="button"
                  disabled={this.state.isAddFileType}
                  data-test-name="file-type-add"
                >
                  <i className="fa fa-plus" />
                </button>
              </div>
              <div className="control">
                <button
                  className="button"
                  type="button"
                  data-test-name="file-type-remove"
                  disabled={!this.state.listboxSelection.selectedItems.length}
                  onClick={this.handleRemoveFileType}
                >
                  <i className="fa fa-minus" />
                </button>
              </div>
              <div className="control">
                <button
                  className="button"
                  type="button"
                  data-test-name="file-type-reset"
                  onClick={this.handleRestoreDefaults}
                >
                  <FormattedMessage
                    {...localizedStrings.FILE_TYPES_LABEL_RESTORE_DEFAULTS}
                  />
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  }
}

export default connect(
  state => ({
    exclusionInfo: getCatalogExclusionInfo(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      restoreFileTypesDefault,
      setExclusionInfo,
      showModalPrompt,
    })
)(injectIntl(FileTypesComponent));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/FileTypesComponent.js